// 根路由：Layout + SessionGuard，dev 模式加载 router devtools。
//
// 主动会话探测：进入根组件后立即 useSession() 拉 /api/session；如果回包
// authenticated=false 且当前不在 /login，立刻 navigate('/login', { from })。
// 这样即使是 / 这种"不发任何 API"的占位页也能跳到登录，不依赖某个子页面
// 偶发拿到 401 才触发 SessionGuard。

import { createRootRoute, Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { ChunkLoadErrorBoundary } from "@/components/ChunkLoadErrorBoundary";
import { SessionGuard } from "@/hooks/useSessionGuard";
import { useSession } from "@/hooks/useSession";
import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";
import { planHashRewrite } from "@/lib/hash-to-search";

const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

function RootComponent() {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isLogin = pathname.startsWith("/login");

  // /login 路由跳过 useSession，避免登录页本身被 redirect 风暴。
  const session = useSession({ enabled: !isLogin });

  // T13 全局快捷键：g d / g m / g a / g i / g g
  useGlobalShortcuts();

  // T9 hash → search 兼容：把老 SSR 的 #tab=xxx 重写到 search params。
  // 用 location 直接读，避免每次路由变化都触发。
  useEffect(() => {
    if (typeof window === "undefined") return;
    const { hash, search } = window.location;
    const plan = planHashRewrite(hash, search);
    if (!plan.rewrote) return;
    const newSearch = new URLSearchParams(plan.search).toString();
    const url =
      window.location.pathname +
      (newSearch ? `?${newSearch}` : "") +
      (plan.hash || "");
    window.history.replaceState(window.history.state, "", url);
    // 通知 router 重新解析 location
    router.navigate({
      to: window.location.pathname,
      search: plan.search,
      hash: plan.hash.startsWith("#") ? plan.hash.slice(1) : plan.hash,
      replace: true,
    });
  }, [router]);

  useEffect(() => {
    if (isLogin) return;
    if (session.data && !session.data.authenticated) {
      const from = pathname + window.location.search;
      router.navigate({ to: "/login", search: { from } });
    }
  }, [session.data, isLogin, pathname, router]);

  // 首次 session 探测中，避免子页面闪过未授权状态。
  if (!isLogin && session.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">
        正在检查会话…
      </div>
    );
  }
  // 已知未登录，等 useEffect 触发 navigate，期间 render null 避免闪烁。
  if (!isLogin && session.data && !session.data.authenticated) {
    return null;
  }

  return (
    <ChunkLoadErrorBoundary>
      <SessionGuard>
        {isLogin ? <Outlet /> : <Layout><Outlet /></Layout>}
        {import.meta.env.DEV ? (
          <Suspense fallback={null}>
            <TanStackRouterDevtools position="bottom-right" />
          </Suspense>
        ) : null}
      </SessionGuard>
    </ChunkLoadErrorBoundary>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
